import { useEffect, useRef } from "react";

import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui";
import type { AgentStoredDocument, XLMModel } from "../api/e2e";
import { formatXLMNumber } from "../lib/xlmDisplay";
import { xlmGuidanceReviewRows } from "../lib/xlmReviews";
import {
  modelReferenceAvailable,
  type ModelReference,
} from "../lib/modelReferences";
import { useAgentDocumentOpener } from "./AgentDocumentActionContext";
import AgentReviewModelLink from "./AgentReviewModelLink";
import AgentSummaryDocumentPreview from "./AgentSummaryDocumentPreview";

const HEADERS = [
  "Line Item",
  "Period",
  "Provider Type",
  "Provider",
  "Isara value",
  "Provider value",
  "Deviation vs. guidance (%)",
  "Direction versus guidance (Absolute)",
  "Explanation",
  "Document reference",
] as const;

function formattedValue(value: number | null, unit: string | null): string {
  return value === null ? "—" : formatXLMNumber(value, unit);
}

export default function AgentGuidanceReviewPanel({
  companyId,
  model,
  documents,
  activeRecordId = null,
  onOpenModelReference,
}: {
  companyId: string;
  model: XLMModel;
  documents: AgentStoredDocument[];
  activeRecordId?: string | null;
  onOpenModelReference: (reference: ModelReference) => void;
}) {
  const {
    closePreview,
    openDocument,
    openingDocumentId: hostOpeningDocumentId,
    previewDocument,
  } = useAgentDocumentOpener();
  const rows = xlmGuidanceReviewRows(model);
  const documentsById = new Map(
    documents.map((document) => [document.id, document]),
  );
  const section = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!activeRecordId) return;
    section.current
      ?.querySelector('[data-review-active="true"]')
      ?.scrollIntoView?.({ block: "center", inline: "nearest" });
  }, [activeRecordId]);

  if (rows.length === 0) {
    return (
      <section className="agent-guidance-review agent-guidance-review--empty">
        This model has no guidance review records.
      </section>
    );
  }

  return (
    <section ref={section} className="agent-guidance-review">
      <TableContainer className="agent-guidance-review__table-container">
        <Table className="agent-guidance-review__table">
          <TableHead>
            <TableRow>
              {HEADERS.map((header) => (
                <TableHeader key={header}>{header}</TableHeader>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const document = row.document
                ? documentsById.get(row.document)
                : undefined;
              const reference = row.reference;
              const referenceAvailable =
                reference !== null && modelReferenceAvailable(model, reference);
              const isaraValue = formattedValue(row.isaraValue, row.unit);
              const cells = [
                referenceAvailable && reference ? (
                  <AgentReviewModelLink
                    reference={reference}
                    title={`Open ${row.item}, ${row.period} in Model`}
                    onOpenModelReference={onOpenModelReference}
                  >
                    {row.item}
                  </AgentReviewModelLink>
                ) : (
                  row.item
                ),
                row.period,
                row.providerType,
                row.provider,
                referenceAvailable && reference ? (
                  <AgentReviewModelLink
                    value
                    reference={reference}
                    title={`Open Isara value ${isaraValue} for ${row.item}, ${row.period} in Model`}
                    onOpenModelReference={onOpenModelReference}
                  >
                    {isaraValue}
                  </AgentReviewModelLink>
                ) : (
                  isaraValue
                ),
                formatXLMNumber(row.providerValue, row.unit, {
                  zeroStyle: "number",
                }),
                formattedValue(row.relativeDeviation, "percent"),
                formattedValue(row.absoluteDirection, row.unit),
                row.explanation,
                document ? (
                  <Button
                    type="button"
                    tone="quiet"
                    size="xs"
                    loading={hostOpeningDocumentId === document.id}
                    onClick={() => openDocument(document)}
                  >
                    {document.name}
                  </Button>
                ) : (
                  "—"
                ),
              ];
              const active = row.id === activeRecordId;
              return (
                <TableRow
                  key={row.id}
                  className={active ? "agent-review-row--active" : undefined}
                  aria-current={active ? "true" : undefined}
                  data-review-active={active ? "true" : undefined}
                >
                  {cells.map((cell, index) => (
                    <TableCell key={HEADERS[index]}>{cell}</TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {previewDocument && (
        <AgentSummaryDocumentPreview
          companyId={companyId}
          document={previewDocument}
          onClose={closePreview}
        />
      )}
    </section>
  );
}
